import { getAccessToken } from "../../services/authApi";
import { fieldReader, fieldWriter } from "../../services/loanApi";
import encompassFields from "../../data/encompass_fields.json";
import encompassFieldsDSCR from "../../data/encompass_fields_DSCR.json";
import {
    generateFilledPDF,
    getExistingDocumentId,
    createDocument,
    getAttachmentUploadUrl,
    uploadAttachmentBlob,
    assignAttachmentToDocument
} from "../../services/documentApi";
import elliemaeApi from "../../services/interceptors/elliemaeApi";
import { LOAN_PROGRAM_CODES } from "../../config/organizations";
import { isLoanProgram } from "../../utils/loanProgramUtil";

const FORM_1008 = "1008 - Transmittal Summary";
const FORM_1003 = "2020 URLA (1003/65) - All Pages";

const POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 3000;

export async function generateAdditionalDocuments(loanId) {
    const authObj = await window.elli.script.getObject("auth");
    const authCode = await authObj.createAuthCode();
    const token = await getAccessToken(authCode);

    if (!token) {
        console.error("Token generation failed.");
        return;
    }

    // Save async process timestamp
    const now = new Date();

    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();

    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours === 0 ? 12 : hours;

    const formattedDate = `${mm}/${dd}/${yyyy} ${hours}:${minutes}:${seconds} ${ampm}`;

    const fieldData = [
        {
            id: "CX.APPROVALCERT.ASYNC.PROC",
            value: formattedDate
        }
    ];

    await fieldWriter(token, loanId, fieldData);
    console.log("CX.APPROVALCERT.ASYNC.PROC updated successfully!");
}

async function createOnDemandOrder(token, loanId, formSource, borrowerId = null) {

    const url = "/encompassdocs/v1/documentOrders/ondemand";

    const body = {
        entity: {
            entityId: loanId,
            entityType: "urn:elli:encompass:loan"
        },
        printMode: "LoanData",
        forms: [{
            source: formSource,
            type: "Standard"
        }]
    };

    if (borrowerId) {
        body.scope = {
            entityId: borrowerId,
            entityType: "urn:elli:encompass:loan:borrower"
        };
    }

    const resp = await elliemaeApi.post(url,
        body, {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    });

    const data = resp.data;
    return data?.id;
}

async function pollAndAdd(token, orderId) {
    const url = `/encompassdocs/v1/documentOrders/ondemand/${orderId}`;

    for (let i = 0; i < POLL_ATTEMPTS; i++) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));

        const resp = await elliemaeApi.get(url, {
            headers: { Authorization: `Bearer ${token}` }
        });

        const data = resp.data;

        if (data.status === "SUCCEEDED") {
            const docIds = data.documents?.map(d => d.id) || [];

            await elliemaeApi.post(`${url}/addToEFolder`, {
                partitionOption: "MultipleDocuments",
                documents: docIds
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            return true;
        }

        if (["FAILED", "CANCELLED"].includes(data.status))
            return false;
    }

    return false;
}

async function generate1008(token, loanId, borrowerId) {

    const orderId = await createOnDemandOrder(
        token,
        loanId,
        FORM_1008,
        borrowerId
    );

    if (!orderId) return;

    await pollAndAdd(token, orderId);
}

async function generate1003(token, loanId, borrowerId) {

    const orderId = await createOnDemandOrder(
        token,
        loanId,
        FORM_1003,
        borrowerId
    );

    if (!orderId) return;

    await pollAndAdd(token, orderId);
}

async function generateCommercialLoanApp(token, loanId, custfieldValues) {

    try {

        const brokerProduct = (custfieldValues["CX.LOAN.PROD.BROKER.SELECT"] || "")
            .toString()
            .trim()
            .toUpperCase();

        const encompassDocType = (custfieldValues["CX.DOC.TYPE"] || "")
            .toString()
            .trim()
            .toUpperCase();

        const isIncomeMode =
            isLoanProgram(brokerProduct, LOAN_PROGRAM_CODES.PROGRAM_003) || // ACCELERATOR INCOME / BREEZY INCOME
            (isLoanProgram(brokerProduct, LOAN_PROGRAM_CODES.PROGRAM_002) && // ACCELERATOR / BREEZY
                (encompassDocType === "FULL DOC" ||
                    encompassDocType === "ALT DOC"));

        const documentationType = isIncomeMode ? "Income" : "DSCR";

        const fieldsJson = documentationType === "Income"
            ? encompassFields
            : encompassFieldsDSCR;

        // Step 2: Collect all field IDs
        const fieldIds = [...new Set(fieldsJson.map(f => f.id))];

        if (!fieldIds.length) {
            console.warn("No field IDs found for Commercial Loan App.");
            return;
        }

        // Step 3: Read all field values from Encompass
        const fieldValues = await fieldReader(token, fieldIds, loanId);

        // Step 4: Map into PDF field structure
        const pdfFields = fieldsJson
            .map(f => {

                let value = fieldValues[f.id] ?? "";

                // Normalize boolean types if needed
                if (f.datatype === "YN") {
                    value = value === true || value === "true" ? "true" :
                        value === false || value === "false" ? "false" :
                            value || "";
                }

                return {
                    id: f.id,
                    value,
                    PDFFieldName: f.PDFFieldName,
                    PDFFieldType: f.PDFFieldType,
                    PDFGroupField: f.PDFGroupField,
                    PDFGroupOrder: f.PDFGroupOrder,
                    Separator: f.Separator
                };
            })
            .filter(f => f.PDFFieldName);

        if (!pdfFields.length) {
            console.warn("No PDF-mapped fields found.");
            return;
        }

        // Step 5: Generate PDF
        const pdfBlob = await generateFilledPDF(pdfFields, documentationType);

        const documentTitle = "Commercial Loan Application";
        const fileName =
            documentationType === "DSCR"
                ? "CommercialApplication – DSCR.pdf"
                : "CommercialApplication – Income.pdf";

        // Step 6: Create or reuse document container
        let documentId = await getExistingDocumentId(token, loanId, documentTitle);

        if (!documentId) {
            documentId = await createDocument(token, loanId, documentTitle);
        }

        // Step 7: Upload PDF
        const uploadInfo = await getAttachmentUploadUrl(
            token,
            loanId,
            fileName,
            pdfBlob.size,
            ""
        );

        await uploadAttachmentBlob(
            uploadInfo.uploadUrl,
            uploadInfo.authorizationHeader,
            pdfBlob,
            ""
        );

        // Step 8: Assign to document
        await assignAttachmentToDocument(
            token,
            loanId,
            documentId,
            uploadInfo.attachmentId
        );

        console.log("Commercial Loan App generated successfully.");

    } catch (error) {
        console.error("Error generating Commercial Loan App:", error);
    }
}